// The consent banner: shown once, before anything leaves the device. Quiet,
// honest, and easy to dismiss either way. Nothing is tracked until a choice
// is made (docs/PRIVACY.md, ANALYTICS.md).

import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/ui/Button";
import { getConsent, setConsent } from "@/analytics/consent";
import { track } from "@/analytics/service";
import { springPanel } from "@/motion/springs";
import "./consent.css";

// Immersive, controls-at-the-edges surfaces where a bottom banner would cover
// creative tools. Consent defaults to off, is offered on entry surfaces, and
// stays reachable from the cookie center, so deferring here changes nothing.
const IMMERSIVE = ["/studio/", "/stage/", "/reflections/wrapped"];

export function ConsentBanner() {
  const { pathname } = useLocation();
  const [show, setShow] = useState(false);
  const immersive = IMMERSIVE.some((p) => pathname.startsWith(p));

  useEffect(() => {
    if (getConsent() === "unset") setShow(true);
  }, []);

  const choose = (level: "all" | "none") => {
    setConsent(level);
    track("consent.updated", { level });
    setShow(false);
  };

  return (
    <AnimatePresence>
      {show && !immersive && (
        <motion.div
          className="bd-consent-banner"
          role="dialog"
          aria-label="Cookie preferences"
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1, transition: springPanel }}
          exit={{ y: 80, opacity: 0, transition: { duration: 0.2 } }}
        >
          <div className="bd-consent-banner__text">
            <strong>Nothing leaves your device without your say.</strong>
            <span>
              Beat Draft works fully offline. Turn on anonymous analytics to help us improve, or keep it to only what the
              product needs. <Link to="/legal/cookies">Read the cookie policy</Link>.
            </span>
          </div>
          <div className="bd-consent-banner__actions">
            <Button variant="ghost" size="sm" onClick={() => choose("none")}>
              Only necessary
            </Button>
            <Button variant="primary" size="sm" onClick={() => choose("all")}>
              Allow analytics
            </Button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
